import { useEffect, useState } from "react";
import { getCashPhotoUploadUrl, putCashPhoto, saveCashPhoto } from "../../../services/cashBookApi";
import type { PhotoFileType, SavePhotoRequest, SavePhotoResponse } from "./types";

/**
 * End-of-day photos wait on the phone until they reach the server (V1_SPEC 11.4 offline queue):
 * IndexedDB `hkp-cashbook` / `photos`, one row per photo `{clientRequestId, blob, meta}`, sent in the
 * order taken (upload-url → PUT → POST photos). A row leaves the phone only when the POST succeeds or
 * says PHOTO_ALREADY_SENT. A refusal that can never succeed (a superseded print, no access) is kept
 * with its reason and skipped, so it never blocks the photos behind it.
 *
 * Each photo belongs to the person who took it (studio + user). The phone may be shared ("I am not
 * …"), so only the signed-in owner's photos are sent (with their own login) or counted, and a logout
 * drops that person's photos from the phone.
 */

export type QueuedPhoto = {
  clientRequestId: string;
  blob: Blob;
  contentType: string;
  fileType: PhotoFileType;
  meta: Omit<SavePhotoRequest, "key" | "clientRequestId">;
  createdAt: number;
  /** Who took it; set by enqueuePhoto from the signed-in user. Others' photos are never sent or shown. */
  ownerUserId?: string | null;
  orgId?: string | null;
  /** Set when the server refused it for good (code). Not retried; shown to the supervisor. */
  failedCode?: string | null;
  failedMessage?: string | null;
};

export interface PhotoStore {
  put(item: QueuedPhoto): Promise<void>;
  all(): Promise<QueuedPhoto[]>;
  remove(clientRequestId: string): Promise<void>;
}

export const memoryStore = (): PhotoStore => {
  const rows = new Map<string, QueuedPhoto>();
  return {
    put: async (item) => {
      rows.set(item.clientRequestId, item);
    },
    all: async () => Array.from(rows.values()).sort((a, b) => a.createdAt - b.createdAt),
    remove: async (id) => {
      rows.delete(id);
    },
  };
};

const DB = "hkp-cashbook";
const STORE = "photos";

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "clientRequestId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async <T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const req = run(t.objectStore(STORE));
      t.oncomplete = () => resolve(req.result);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  } finally {
    db.close();
  }
};

/** The phone's IndexedDB; a phone without it (private mode, old browser) keeps photos in memory. */
export const idbStore = (): PhotoStore => {
  if (typeof indexedDB === "undefined") return memoryStore();
  const fallback = memoryStore();
  let broken = false;
  return {
    put: async (item) => {
      if (broken) return fallback.put(item);
      try {
        await tx("readwrite", (s) => s.put(item));
      } catch {
        broken = true;
        await fallback.put(item);
      }
    },
    all: async () => {
      if (broken) return fallback.all();
      try {
        const rows = (await tx<QueuedPhoto[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedPhoto[]>)) || [];
        return rows.sort((a, b) => a.createdAt - b.createdAt);
      } catch {
        broken = true;
        return fallback.all();
      }
    },
    remove: async (id) => {
      if (broken) return fallback.remove(id);
      try {
        await tx("readwrite", (s) => s.delete(id));
      } catch {
        broken = true;
        await fallback.remove(id);
      }
    },
  };
};

/** The signed-in person (studio + user), or null when nobody is signed in. */
export const photoOwner = (): { userId: string; orgId: string } | null => {
  try {
    const u = JSON.parse(localStorage.getItem("authUser") || "null")?.users;
    const userId = u?._id || u?.id || u?.userId;
    return userId ? { userId: String(userId), orgId: String(u.organizationResponse?._id || "") } : null;
  } catch {
    return null;
  }
};

const mine = (row: QueuedPhoto, me: { userId: string; orgId: string } | null): boolean =>
  !!me && row.ownerUserId === me.userId && (row.orgId || "") === me.orgId;

let store: PhotoStore | null = null;
const getStore = () => (store = store || idbStore());
/** Tests: use an in-memory store. */
export const setPhotoStore = (s: PhotoStore | null) => {
  store = s;
};

export type UploadOutcome =
  | { state: "sent"; response: SavePhotoResponse }
  | { state: "alreadySent"; photoId: string | null; uploadedAt: string | null }
  | { state: "queued" }
  /** `retry`: the photo stays in the queue and goes later (storage not set up yet); else it is final. */
  | { state: "refused"; code: string; message: string; data?: any; retry: boolean };

/** Codes worth trying again later without telling anyone; everything else from the server is final. */
const RETRY_CODES = new Set(["KEY_INVALID", "BUSY"]);
/** Failed photos are kept 14 days (the reminder window), then dropped from the phone. */
const KEEP_FAILED_MS = 14 * 86400000;

export type QueueApi = {
  uploadUrl: typeof getCashPhotoUploadUrl;
  put: typeof putCashPhoto;
  save: typeof saveCashPhoto;
};
const defaultApi: QueueApi = { uploadUrl: getCashPhotoUploadUrl, put: putCashPhoto, save: saveCashPhoto };

const refusal = (res: any): UploadOutcome => {
  const code: string = res?.code || "";
  if (!code || RETRY_CODES.has(code)) return { state: "queued" };
  return { state: "refused", code, message: res?.message || "", data: res?.data, retry: code === "STORAGE_NOT_SET" };
};

/** Send one photo through the three steps. Network trouble → "queued" (stays on the phone). */
export const sendOne = async (item: QueuedPhoto, api: QueueApi = defaultApi): Promise<UploadOutcome> => {
  try {
    const url = await api.uploadUrl({ kind: item.meta.kind, fileType: item.fileType });
    if (!url?.success || !url.data?.uploadUrl) return refusal(url);
    try {
      await api.put(url.data.uploadUrl, item.blob, item.contentType);
    } catch {
      return { state: "queued" };
    }
    const res = await api.save({ ...item.meta, key: url.data.key, clientRequestId: item.clientRequestId });
    if (res?.success) return { state: "sent", response: res.data };
    if (res?.code === "PHOTO_ALREADY_SENT") {
      const d: any = res.data || {};
      return { state: "alreadySent", photoId: d.photoId ?? null, uploadedAt: d.uploadedAt ?? null };
    }
    return refusal(res);
  } catch {
    return { state: "queued" };
  }
};

const listeners = new Set<(n: number) => void>();
const failedListeners = new Set<(rows: QueuedPhoto[]) => void>();
let pendingCount = 0;
let failedRows: QueuedPhoto[] = [];
const publish = async () => {
  const me = photoOwner();
  const rows = (await getStore().all()).filter((r) => mine(r, me));
  pendingCount = rows.filter((r) => !r.failedCode).length;
  failedRows = rows.filter((r) => !!r.failedCode);
  listeners.forEach((l) => l(pendingCount));
  failedListeners.forEach((l) => l(failedRows));
};

/** Apply one outcome to the stored row. True when the queue should stop (network down). */
const settle = async (row: QueuedPhoto, out: UploadOutcome): Promise<boolean> => {
  if (out.state === "queued") return true;
  if (out.state === "sent" || out.state === "alreadySent") await getStore().remove(row.clientRequestId);
  else if (!out.retry) await getStore().put({ ...row, failedCode: out.code, failedMessage: out.message });
  return out.state === "refused" && out.retry;
};

/** Send the waiting photos older than `upTo` (all when null), in order; stop at the first network failure. */
const sendWaiting = async (api: QueueApi, skip: string | null): Promise<boolean> => {
  const now = Date.now();
  const me = photoOwner();
  for (const row of await getStore().all()) {
    if (row.clientRequestId === skip) continue;
    // Someone else's photo on a shared phone waits for them; never sent with this login.
    if (!mine(row, me)) continue;
    if (row.failedCode) {
      if (now - row.createdAt > KEEP_FAILED_MS) await getStore().remove(row.clientRequestId);
      continue;
    }
    if (await settle(row, await sendOne(row, api))) return false;
  }
  return true;
};

let flushing: Promise<void> | null = null;

/** Send every waiting photo in order; stops at the first network failure (the rest wait too). */
export const flushPhotoQueue = (api: QueueApi = defaultApi): Promise<void> => {
  if (flushing) return flushing;
  flushing = sendWaiting(api, null)
    .then(publish)
    .finally(() => {
      flushing = null;
    });
  return flushing;
};

/**
 * Keep the photo on the phone, then try to send it now, after any older waiting photos (pages arrive
 * in the order taken). Returns what happened to this photo; "queued" means it waits for the network.
 */
export const enqueuePhoto = async (photo: QueuedPhoto, api: QueueApi = defaultApi): Promise<UploadOutcome> => {
  const me = photoOwner();
  const item: QueuedPhoto = { ...photo, ownerUserId: me?.userId ?? null, orgId: me?.orgId ?? null };
  await getStore().put(item);
  if (flushing) await flushing.catch(() => undefined);
  let out: UploadOutcome = { state: "queued" };
  if (await sendWaiting(api, item.clientRequestId)) {
    out = await sendOne(item, api);
    await settle(item, out);
  }
  await publish();
  return out;
};

/** Photos still waiting for the network (for "फ़ोन में सेव · {n} फ़ोटो…"). */
export const usePendingPhotos = (): number => {
  const [n, setN] = useState(pendingCount);
  useEffect(() => {
    listeners.add(setN);
    publish().catch(() => undefined);
    return () => {
      listeners.delete(setN);
    };
  }, []);
  return n;
};

/** The signed-in person's photos the server refused for good (newest first), for the "not sent" card. */
export const useFailedPhotos = (): QueuedPhoto[] => {
  const [rows, setRows] = useState<QueuedPhoto[]>(failedRows);
  useEffect(() => {
    failedListeners.add(setRows);
    publish().catch(() => undefined);
    return () => {
      failedListeners.delete(setRows);
    };
  }, []);
  return [...rows].sort((a, b) => b.createdAt - a.createdAt);
};

/** Drop one photo from the phone (the supervisor chose Remove on a refused photo). */
export const removeQueuedPhoto = async (clientRequestId: string): Promise<void> => {
  const me = photoOwner();
  const row = (await getStore().all()).find((r) => r.clientRequestId === clientRequestId);
  if (row && mine(row, me)) await getStore().remove(clientRequestId);
  await publish();
};

/** The signed-in person's photos still on the phone and not yet sent (for the logout warning). */
export const unsentPhotoCount = async (): Promise<number> => {
  try {
    const me = photoOwner();
    return (await getStore().all()).filter((r) => mine(r, me) && !r.failedCode).length;
  } catch {
    return 0;
  }
};

/** Logout: the signed-in person's photos leave the phone (signed wage sheets are personal data). */
export const clearMyPhotos = async (): Promise<void> => {
  try {
    const me = photoOwner();
    if (!me) return;
    for (const row of await getStore().all()) if (mine(row, me)) await getStore().remove(row.clientRequestId);
    pendingCount = 0;
    failedRows = [];
    listeners.forEach((l) => l(0));
    failedListeners.forEach((l) => l([]));
  } catch {
    // Storage blocked: nothing kept anyway.
  }
};

let started = false;
/** On app open and whenever the network comes back, send what is waiting. Safe to call many times. */
export const startPhotoQueue = () => {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("online", () => {
    flushPhotoQueue().catch(() => undefined);
  });
  flushPhotoQueue().catch(() => undefined);
};
